import { getInitData } from './telegram'

// The API's origin in production (.env.production); empty in development, where the dev server proxies
// same-origin /api. A trailing slash is dropped so paths join as <origin>/api/...
const BASE = (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/+$/, '')

export type LicenseState = 'Active' | 'Expired' | 'Revoked' | 'None'
/** Mirrors GameSessionState in Core — the server serialises the enum by name. */
export type SessionState = 'Valid' | 'ExpiringSoon' | 'Expired' | 'Missing' | 'NeedsCredential'

/** The kinds this build of the panel knows how to describe. */
export const KNOWN_WORKER_KINDS = ['Works', 'Dice', 'Businesses', 'Combat', 'Bosses'] as const
export type KnownWorkerKind = (typeof KNOWN_WORKER_KINDS)[number]
/**
 * The server lists one row per member of its own enum, so a deployed panel can meet a kind it was
 * not built with (a new worker shipped before the bundle, or a cached old bundle). The type admits
 * that, and the UI falls back to a generic label instead of dereferencing `undefined`.
 */
export type WorkerKind = KnownWorkerKind | (string & {})

export interface License {
  state: LicenseState
  tier: 'Trial' | 'Standard'
  expiresAtUtc: string | null
  isActive: boolean
}

export interface LoginResponse {
  accessToken: string
  expiresAtUtc: string
  user: { id: number; username: string | null; firstName: string | null }
  license: License
  /** The bot this panel belongs to, so "Открыть бота" never links to a bare t.me/. */
  botUsername: string | null
}

export interface Account {
  telegramUserId: number
  /** The Telegram account is connected: its session is stored and the bot renews the game pass from it. */
  connected?: boolean
  /** Connecting works on this server (database, master key, Telegram app configured). */
  connectAvailable?: boolean
  gameSession: {
    state: SessionState
    expiresAtUtc: string | null
    remainingSeconds: number | null
    needsUserAction: boolean
    /** A provider can mint a new token without the user (the bot renews it). */
    renewable: boolean
  }
  license: License
}

export interface Worker {
  kind: WorkerKind
  enabled: boolean
  /** Null when the server did not say: no chip is highlighted rather than a made-up one. */
  intervalMinutes: number | null
  nextRunAtUtc: string | null
  lastRunAtUtc: string | null
  lastOutcome: string | null
  consecutiveFailures: number
}

export interface WorkersView {
  /**
   * False when the admin paused the whole account: every row keeps its own `enabled` flag, but the
   * scheduler runs none of them until the account is switched back on.
   */
  accountEnabled: boolean
  workers: Worker[]
}

/** What to ask for next while connecting: nothing in progress, the code Telegram sent, the cloud password. */
export type ConnectStep = 'phone' | 'code' | 'password' | 'done'

export interface ConnectStatus {
  available: boolean
  connected: boolean
  step: ConnectStep
}

export interface ConnectResult {
  step: ConnectStep
  /** Why the last step did not go through; `error` says it in Russian. */
  errorCode: string | null
  error: string | null
  /** The code or password was wrong and Telegram gives another try. */
  repeated: boolean
  /** After `done`: whether the game pass was obtained now, will be at the first run, or was refused. */
  game: 'ok' | 'pending' | 'failed' | null
  message: string | null
}

export interface Plan {
  id: string
  name: string
  description: string
  priceStars: number
  durationDays: number
  recurring: boolean
}

export interface SubscriptionView {
  enabled: boolean
  available: boolean
  plans: Plan[]
  autoRenew: { count: number; nextChargeAtUtc: string | null } | null
}

export interface RenewalCancel {
  live: number
  stopped: number
  failed: number
  activeUntilUtc: string | null
}

export interface Payment {
  chargeId: string
  planName: string
  amountStars: number
  durationDays: number
  recurring: boolean
  status: 'paid' | 'refunded'
  paidAtUtc: string
  refundedAtUtc: string | null
}

export interface Info {
  terms: { title: string; points: string[]; acceptance: string }
  support: { title: string; lines: string[]; contact: string | null; url: string | null }
}

/** A failed call, carrying the status so callers can tell "pay up" from "broken". */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message?: string,
  ) {
    super(message ?? code)
  }

  /** The licence does not allow this. */
  get isPaymentRequired() {
    return this.status === 402
  }

  /**
   * The server is up but running without a database. Only the API's own 503 says so — a reverse
   * proxy answers 503 too while the API restarts, and that one has no database to be missing.
   */
  get isUnavailable() {
    return this.status === 503 && this.code === 'schedule_store_unavailable'
  }

  /**
   * A gateway answered for an API that is not there right now: a restart, an overload. Nothing about
   * the account changed, so the caller keeps what it has and tries again later. 520–530 are
   * Cloudflare's own answers when the site behind it is down, unreachable or too slow.
   */
  get isServerRestarting() {
    const s = this.status
    return (s === 502 || s === 503 || s === 504 || (s >= 520 && s <= 530)) && !this.isUnavailable
  }

  /** The request never reached the server — no connection, DNS, CORS. */
  get isOffline() {
    return this.status === 0 && this.code === 'network'
  }

  /**
   * The panel token expired and the initData is too old to mint another. Only a fresh launch from
   * Telegram fixes this; retrying will not.
   */
  get isSessionExpired() {
    return this.code === 'session_expired'
  }

  /** Too many attempts; the server said how long to wait (seconds), when it did. */
  retryAfterSeconds: number | null = null

  get isRateLimited() {
    return this.status === 429
  }
}

/**
 * `fetch` rejects rather than resolving when a request never reaches the server, and its message
 * ("Failed to fetch") is not something to show anyone. Turned into an ApiError so every caller has
 * one error type to handle.
 */
async function send(url: string, init?: RequestInit): Promise<Response> {
  try {
    return await fetch(url, init)
  } catch {
    throw new ApiError(0, 'network', 'Нет связи с сервером')
  }
}

/**
 * The panel token, held in memory only.
 *
 * Not localStorage: this runs in a WebView, the token is a bearer credential, and re-login is a
 * single request against an initData we are handed for free on every load. Persisting it would buy
 * nothing and leave a credential lying around.
 */
let token: string | null = null

export async function login(): Promise<LoginResponse> {
  const initData = getInitData()
  if (!initData) {
    throw new ApiError(0, 'no_init_data', 'Откройте панель через Telegram')
  }

  const response = await send(`${BASE}/api/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ initData }),
  })

  const body = await readBody(response)
  if (!response.ok) {
    throw new ApiError(response.status, body?.error ?? 'login_failed')
  }

  token = body.accessToken
  return body as LoginResponse
}

async function request<T>(path: string, init?: RequestInit, isRetry = false): Promise<T> {
  if (!token) await login()

  const response = await send(`${BASE}${path}`, {
    ...init,
    headers: {
      ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
      ...init?.headers,
      Authorization: `Bearer ${token}`,
    },
  })

  // The token expires, so a panel left open will eventually meet a 401. Re-login once and retry
  // rather than showing the user an error for something we can fix silently. When the re-login
  // itself is refused (the initData has aged past the server's limit too), say so in one clear
  // error instead of leaking the validator's wording into a countdown that silently stops updating.
  if (response.status === 401 && !isRetry) {
    token = null
    try {
      await login()
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) throw new ApiError(401, 'session_expired', 'Сессия панели устарела')
      throw e
    }
    return request<T>(path, init, true)
  }

  const body = await readBody(response)
  if (!response.ok) {
    // `message` is the server's Russian explanation for the customer, where it gives one.
    const error = new ApiError(response.status, body?.error ?? `http_${response.status}`, body?.message ?? body?.detail)
    const retryAfter = Number(response.headers.get('Retry-After'))
    if (Number.isFinite(retryAfter) && retryAfter > 0) error.retryAfterSeconds = retryAfter
    throw error
  }

  return body as T
}

/**
 * The API always answers JSON. Anything else is a page from something in front of it — IIS while the
 * site restarts, a CDN's error or challenge page — and its HTML is never shown to anyone: the status
 * is all the caller gets.
 */
async function readBody(response: Response): Promise<any> {
  const text = await response.text()
  if (!text) return null
  try {
    return JSON.parse(text)
  } catch {
    return { error: `http_${response.status}` }
  }
}

/**
 * GET /api/workers has three shapes in the wild: a bare array (older servers), an envelope
 * `{ accountEnabled, workers }`, and rows each carrying their own `accountEnabled`. Nobody past
 * this function should care which one answered.
 */
export function normalizeWorkers(body: unknown): WorkersView {
  const rows: unknown[] = Array.isArray(body)
    ? body
    : Array.isArray((body as { workers?: unknown })?.workers)
      ? (body as { workers: unknown[] }).workers
      : []

  const topLevel = !Array.isArray(body) ? (body as { accountEnabled?: unknown })?.accountEnabled : undefined
  // A flag on any row is the same account-wide fact; one `false` is enough to mean paused.
  const perRow = rows.map((r) => (r as { accountEnabled?: unknown })?.accountEnabled).filter((v) => typeof v === 'boolean')
  const accountEnabled =
    typeof topLevel === 'boolean' ? topLevel : perRow.length > 0 ? perRow.every((v) => v === true) : true

  const workers = rows
    .filter((r): r is Record<string, unknown> => !!r && typeof r === 'object' && typeof (r as { kind?: unknown }).kind === 'string')
    .map((r) => ({
      kind: r.kind as string,
      enabled: r.enabled === true,
      // Not defaulted: a chip lit for an interval nobody set would read as the real setting.
      intervalMinutes: typeof r.intervalMinutes === 'number' ? r.intervalMinutes : null,
      nextRunAtUtc: typeof r.nextRunAtUtc === 'string' ? r.nextRunAtUtc : null,
      lastRunAtUtc: typeof r.lastRunAtUtc === 'string' ? r.lastRunAtUtc : null,
      lastOutcome: typeof r.lastOutcome === 'string' ? r.lastOutcome : null,
      consecutiveFailures: typeof r.consecutiveFailures === 'number' ? r.consecutiveFailures : 0,
    }))

  return { accountEnabled, workers }
}

export const api = {
  getAccount: () => request<Account>('/api/account'),
  getWorkers: async () => normalizeWorkers(await request<unknown>('/api/workers')),
  setWorkerEnabled: (kind: WorkerKind, enabled: boolean) =>
    request<{ kind: WorkerKind; enabled: boolean }>(`/api/workers/${kind}`, {
      method: 'PUT',
      body: JSON.stringify({ enabled }),
    }),
  /**
   * Deliberately carries no `enabled`: the server keeps the flag as it is. Sending the panel's copy
   * would re-enable a worker the user had just switched off through the bot, because that copy is
   * only as fresh as the last refresh.
   */
  setWorkerInterval: (kind: WorkerKind, intervalMinutes: number) =>
    request<{ kind: WorkerKind; enabled: boolean }>(`/api/workers/${kind}`, {
      method: 'PUT',
      body: JSON.stringify({ intervalMinutes }),
    }),

  // --- connecting the Telegram account (what is typed goes to the server, which hands it to Telegram) ---
  getConnect: () => request<ConnectStatus>('/api/connect'),
  connectStart: (phone: string, consent: boolean) =>
    request<ConnectResult>('/api/connect/start', { method: 'POST', body: JSON.stringify({ phone, consent }) }),
  connectCode: (code: string) => request<ConnectResult>('/api/connect/code', { method: 'POST', body: JSON.stringify({ code }) }),
  connectPassword: (password: string) =>
    request<ConnectResult>('/api/connect/password', { method: 'POST', body: JSON.stringify({ password }) }),
  connectCancel: () => request<null>('/api/connect/cancel', { method: 'POST' }),
  disconnect: () => request<{ connected: boolean }>('/api/connect/disconnect', { method: 'POST' }),

  // --- the subscription -------------------------------------------------------------------------------
  getSubscription: () => request<SubscriptionView>('/api/subscription'),
  createInvoice: (planId: string) =>
    request<{ invoiceLink: string }>('/api/subscription/invoice', { method: 'POST', body: JSON.stringify({ planId }) }),
  cancelRenewal: () => request<RenewalCancel>('/api/subscription/cancel-renewal', { method: 'POST' }),
  getPayments: () => request<Payment[]>('/api/payments'),
  getInfo: () => request<Info>('/api/info'),
}
